// TaskDetail (M1 §8): checklist, comentários, evidência, HistoryTimeline. Ações via WorkItemActionBar.
import { Button, Checkbox, Input } from '@giyus/ui';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { HistoryTimeline } from '../../../components';
import { useDateFormatter } from '../../../lib/dates/useDateFormatter';
import { apiScopePath } from '../../../lib/workflow/itemRoutes';
import type { WorkflowTaskDetail } from '../api/contract.preview';
import { useAddTaskComment, useTaskAction, useToggleChecklistItem } from '../api/queries';
import { WorkItemActionBar } from './WorkItemActionBar';

export interface TaskDetailProps {
  readonly task: WorkflowTaskDetail;
}

export function TaskDetail({ task }: TaskDetailProps) {
  const { t } = useTranslation(['workflow', 'common']);
  const dates = useDateFormatter();
  const scopePath = apiScopePath(task.scope);
  const taskAction = useTaskAction();
  const toggleChecklist = useToggleChecklistItem();
  const addComment = useAddTaskComment();
  const [comment, setComment] = useState('');
  const [reasonPrompt, setReasonPrompt] = useState<'block' | 'cancel' | null>(null);
  const [reason, setReason] = useState('');

  function runAction(action: 'claim' | 'start' | 'complete' | 'block' | 'cancel'): void {
    if (action === 'block' || action === 'cancel') {
      setReasonPrompt(action);
      return;
    }
    taskAction.mutate({ scopePath, taskId: task.id, action });
  }

  function confirmReason(): void {
    if (!reasonPrompt || !reason.trim()) {
      return;
    }
    taskAction.mutate({ scopePath, taskId: task.id, action: reasonPrompt, reason: reason.trim() });
    setReasonPrompt(null);
    setReason('');
  }

  return (
    <div className="flex flex-col gap-4" data-testid="task-detail">
      <div>
        <h1 className="text-title font-semibold">{task.subject ?? t('task.detail.title')}</h1>
        {task.description && <p className="text-body text-muted-foreground">{task.description}</p>}
        <p className="mt-1 text-body text-muted-foreground">
          {task.assigneeName
            ? t('task.detail.claimedBy', { name: task.assigneeName })
            : t('task.detail.unclaimed')}
        </p>
        {task.blockedReason && <p className="text-body text-destructive">{task.blockedReason}</p>}
        {task.requiresEvidence && !task.evidencePresent && (
          <p
            className="text-body text-warning-subtle-foreground"
            data-testid="task-evidence-warning"
          >
            {t('task.detail.evidenceRequired')}
          </p>
        )}
      </div>

      <WorkItemActionBar
        scope={task.scope}
        allowedActions={task.allowedActions}
        pending={taskAction.isPending ? taskAction.variables.action : null}
        onAction={(action) => {
          // O servidor só devolve ações válidas para uma tarefa (`taskAllowedActions`); as demais (aprovação/
          // exceção) nunca aparecem aqui — o `switch` sem `default` documenta essa garantia sem afirmá-la em tipo.
          switch (action) {
            case 'claim':
            case 'start':
            case 'complete':
            case 'block':
            case 'cancel':
              runAction(action);
              break;
          }
        }}
      />

      {reasonPrompt && (
        <div
          className="flex flex-col gap-2 rounded-card border p-3"
          data-testid="task-reason-prompt"
        >
          <label htmlFor="task-reason" className="text-body font-medium">
            {reasonPrompt === 'block'
              ? t('task.detail.blockReasonLabel')
              : t('task.detail.cancelReasonLabel')}
          </label>
          <Input
            id="task-reason"
            value={reason}
            onChange={(e) => {
              setReason(e.target.value);
            }}
          />
          <div className="flex gap-2">
            <Button size="sm" onClick={confirmReason} disabled={!reason.trim()}>
              {t('common:actions.confirm')}
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={() => {
                setReasonPrompt(null);
                setReason('');
              }}
            >
              {t('common:actions.cancel')}
            </Button>
          </div>
        </div>
      )}

      {task.checklist.length > 0 && (
        <section>
          <h2 className="mb-2 text-body font-semibold">{t('task.detail.checklist')}</h2>
          <ul className="flex flex-col gap-2">
            {task.checklist.map((item) => (
              <li key={item.id} className="flex items-center gap-2 text-body">
                <Checkbox
                  id={`checklist-${item.id}`}
                  checked={item.isChecked}
                  onCheckedChange={(checked) => {
                    toggleChecklist.mutate({
                      scopePath,
                      taskId: task.id,
                      itemId: item.id,
                      isChecked: checked === true,
                    });
                  }}
                />
                <label htmlFor={`checklist-${item.id}`}>
                  {item.label ??
                    t(`task.detail.checklistItems.${item.key ?? ''}`, {
                      defaultValue: item.key ?? '',
                    })}
                  {item.isRequired && ' *'}
                </label>
              </li>
            ))}
          </ul>
        </section>
      )}

      <section>
        <h2 className="mb-2 text-body font-semibold">{t('task.detail.comments')}</h2>
        {task.comments.length === 0 ? (
          <p className="text-body text-muted-foreground">{t('task.detail.commentsEmpty')}</p>
        ) : (
          <ul className="flex flex-col gap-2">
            {task.comments.map((item) => (
              <li key={item.id} className="rounded-card border p-2 text-body">
                <p className="font-medium">{item.authorName}</p>
                <p>{item.body}</p>
                <p className="text-caption text-muted-foreground">
                  {dates.dateTime(item.createdAt)}
                </p>
              </li>
            ))}
          </ul>
        )}
        <form
          className="mt-2 flex gap-2"
          onSubmit={(e) => {
            e.preventDefault();
            if (!comment.trim()) {
              return;
            }
            addComment.mutate({ scopePath, taskId: task.id, body: comment.trim() });
            setComment('');
          }}
        >
          <label htmlFor="task-comment" className="sr-only">
            {t('task.detail.commentPlaceholder')}
          </label>
          <Input
            id="task-comment"
            value={comment}
            placeholder={t('task.detail.commentPlaceholder')}
            onChange={(e) => {
              setComment(e.target.value);
            }}
          />
          <Button type="submit" size="sm" disabled={!comment.trim()}>
            {t('actions.addComment')}
          </Button>
        </form>
      </section>

      <HistoryTimeline
        entries={task.history.map((h) => ({
          id: h.id,
          action: h.action,
          actorName: h.actorName,
          actorRole: h.actorRole,
          occurredAt: dates.dateTime(h.occurredAt),
          reason: h.reason ?? null,
          summary: h.summary ?? null,
        }))}
      />
    </div>
  );
}
